// TSIA2 score report, tutor side.
//
// The student page, reading the tutor endpoint. Kept as a copy so the two
// reports cannot drift into saying different things about one attempt.
//
// Structure, class names and components follow DigitalSHSATReport so this
// reads as the same report a student sees for every other test type: the back
// bar, the logo/name/date header, "Test Result Overview" with REVIEW QUESTIONS
// beside it, the horizontal score band, then Category Scores with the Level 1
// bars expanding to their Level 2 rows on click.
//
// What is deliberately absent: every timing panel the other reports carry --
// the Section Time Averages gauges, the per-question time targets, the
// time-spent colouring. TSIA2 is untimed, so pace is not a thing that
// happened, and a panel reporting on it would be inventing data.
//
// In its place, each section names the stages the student was actually served.
// That is the fact the timing panel used to occupy space with: whether the
// student met the CRC cutoff or was routed into the Diagnostic.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdOutlineArrowBack } from 'react-icons/md';
import moment from 'moment';

import axios from 'config/axios';
import Button from 'components/Button2';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import Loader from 'components/Loader';

import './index.css';

// Declared here because the tutor app keeps this per file rather than in
// a global module.
const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D'];

type Category = { name: string; numCorrect: number; numQuestions: number };

type RootCategory = Category & { categories: Category[] };

type AnswerRow = {
    questionNumber: number;
    choice: number | null;
    correctChoice: number | null;
    isCorrect: boolean;
};

type Stage = {
    moduleName: string;
    moduleNumber: number;
    correct: number;
    total: number;
    // Per stage, not per section: weak categories on the stage that routed a
    // student are a different question from weak categories on the stage they
    // were routed into, and averaging the two hides both.
    rootCategories: RootCategory[];
    answers: AnswerRow[];
};

type ReportSection = {
    subject: string;
    sectionName: string;
    rawCorrect: number;
    questionCount: number;
    score: number | null;
    stages: Stage[];
};

type ReportData = {
    studentTestId: string;
    name: string;
    studentName: string;
    logo: string | null;
    testDate: string;
    score: number | null;
    rawCorrect: number;
    totalQuestionCount: number;
    testSections: ReportSection[];
};

function percentOf(correct: number, total: number) {
    return total ? Math.round((correct / total) * 100) : 0;
}

type TestTSIA2ReportProps = {
    // Bulk print mounts this component directly instead of routing to it, so
    // the test id has to be able to arrive as a prop as well as a URL param.
    testIdOverride?: string;
    // Rendered inside the bulk-print container: drop the page chrome (document
    // title, back bar, action buttons) so the sheet carries report content only.
    embedded?: boolean;
    // Fires once this report's data has settled, so a bulk print can wait for
    // every report to arrive before opening the print dialog.
    onReady?: () => void;
};

function TestTSIA2Report({ testIdOverride, embedded = false, onReady }: TestTSIA2ReportProps = {}) {
    const [testData, setTestData] = useState<ReportData | null>(null);
    // Which Level 1's Level 2 breakdown is expanded, mirroring the SAT and
    // SHSAT reports.
    const [rootCategoryName, setRootCategoryName] = useState('');
    // Which section and which of its stages the Questions Overview is showing.
    const [sectionIndex, setSectionIndex] = useState(0);
    const [stageIndex, setStageIndex] = useState(0);

    const navigate = useNavigate();
    const { testId: routeTestId } = useParams();
    const testId = testIdOverride ?? routeTestId;
    const printRef = useRef<HTMLDivElement>(null);

    // Print the live DOM natively, the way testSHSATReport does. The @media
    // print stylesheet isolates the report from the app shell, so the printout
    // mirrors the on-screen overview.
    const handlePrint = () => {
        // Scope print isolation to this page only: the @media print rules key
        // off this body class, so other pages' printing is never affected.
        document.body.classList.add('printing-tsia2-test-report');
        // Remove the browser's default page margins for this print only, which
        // also drops the auto-added header/footer (date, title, URL, page #).
        // Injected here and removed on afterprint so other prints keep theirs.
        const pageStyle = document.createElement('style');
        pageStyle.textContent = '@page { margin: 0; }';
        document.head.appendChild(pageStyle);
        const cleanup = () => {
            document.body.classList.remove('printing-tsia2-test-report');
            pageStyle.remove();
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        window.print();
    };

    // `onReady` is read through a ref so a parent passing a fresh closure each
    // render cannot retrigger the fetch -- the effect stays keyed on the id.
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    useEffect(() => {
        if (!testId) return;
        let cancelled = false;
        // A navigation between two report URLs keeps this component mounted;
        // clear the previous student's data rather than show it while the
        // next loads.
        setTestData(null);
        setSectionIndex(0);
        setStageIndex(0);
        axios.get(`tests/digital/tsia2ResultTutor/${testId}/`)
            .then(response => {
                if (cancelled) return;
                setTestData(response.data);
                onReadyRef.current?.();
            })
            .catch(error => {
                if (cancelled) return;
                console.error(error);
                // Report ready even on failure: one unreachable report must not
                // leave a bulk print waiting for a dialog that never opens. On
                // its own page the report still backs out as before.
                onReadyRef.current?.();
                if (!embedded) navigate(-1);
            });
        return () => { cancelled = true; };
    }, [testId, navigate, embedded]);

    // Inside a bulk sheet a full-page loader would paint over the sibling
    // reports; the sheet shows its own "Loading N of M" line instead.
    if (!testData) return embedded ? <></> : <Loader />;

    const activeSection = testData.testSections[sectionIndex] ?? testData.testSections[0];
    const activeStage = activeSection?.stages[stageIndex] ?? activeSection?.stages[0];
    // Ungated here: on the tutor side the key is always visible, as it is on
    // the tutor's other reports. The customization gates the STUDENT's view.
    const showAnswers = true;

    // One renderer feeds the on-screen table (one stage at a time) and the
    // print-only tables (every served stage), so the two cannot drift. On
    // paper the header sits in <thead> so it repeats when a 48-row Diagnostic
    // runs past a sheet; on screen it stays inside <tbody> as the other
    // reports have it.
    const answerRows = (answers: AnswerRow[]) => answers.map(answer => (
        <tr key={answer.questionNumber}>
            <td>{answer.questionNumber}</td>
            {showAnswers && (
                <td>
                    {answer.correctChoice !== null
                        ? MULTIPLE_CHOICES[answer.correctChoice]
                        : '-'}
                </td>
            )}
            <td className={answer.isCorrect
                ? 'digitalTSIA2Report__answerCorrect'
                : 'digitalTSIA2Report__answerIncorrect'}
            >
                {answer.choice !== null
                    ? MULTIPLE_CHOICES[answer.choice]
                    : '-'}
            </td>
        </tr>
    ));
    const answerHead = (
        <tr>
            <th>Question</th>
            {showAnswers && <th>Correct Answer</th>}
            <th>Your Answer</th>
        </tr>
    );

    return (
        // The student app wraps its reports in a shared `layout` class; the
        // tutor app has no such class -- its pages use a plain root and the
        // app shell handles spacing, as testSHSATReport does.
        <div className='digitalTSIA2Report tsia2TutorPalette' ref={printRef}>
            {!embedded && <Helmet><title>{testData.name} Report</title></Helmet>}

            {!embedded && (
                <div className='digitalTSIA2Report__bar'>
                    <button className='digitalTSIA2Report__back' onClick={() => navigate(-1)}>
                        <MdOutlineArrowBack className='digitalTSIA2Report__backIcon' />
                        Back
                    </button>
                </div>
            )}

            <div className='digitalTSIA2Report__headerGroup'>
                <div className='digitalTSIA2Report__headerSection'>
                    {testData.logo && (
                        <img className='digitalTSIA2Report__headerLogo' alt='logo' src={testData.logo} />
                    )}
                </div>
                <div className='digitalTSIA2Report__headerSection'>
                    <div className='digitalTSIA2Report__headerInfo'>
                        <p>Name: {testData.studentName}</p>
                        <p>Test name: {testData.name}</p>
                        <p>Tested on: {moment(testData.testDate).format('MMMM D YYYY')}</p>
                    </div>
                </div>
            </div>

            <div className='digitalTSIA2Report__headerGroup'>
                <h1 className='digitalTSIA2Report__headerTitle'>Test Result Overview</h1>
                {/* Interactive chrome: not rendered inside a bulk sheet, and
                    hidden by the print stylesheet on a single-report print. */}
                {!embedded && (
                    <div className='digitalTSIA2Report__buttonGroup'>
                        <ButtonLink
                            className='digitalTSIA2Report__button'
                            to={`/review/digital-tsia2/${testId}`}
                        >
                            REVIEW QUESTIONS
                        </ButtonLink>
                        <Button className='digitalTSIA2Report__button' secondary onClick={handlePrint}>
                            PRINT REPORT
                        </Button>
                    </div>
                )}
            </div>

            <div className='digitalTSIA2Report__card'>
                <div className='digitalTSIA2Report__cardRow'>
                    {/* Score band: the whole test, then each section. TSIA2 is
                        scored as a flat percentage, so the headline is the
                        percent with the raw count beneath it -- no scaled
                        score, because the test has none. */}
                    <div className='digitalTSIA2Report__scoresGroup'>
                        <div className='digitalTSIA2Report__scoreBlock'>
                            <h2 className='digitalTSIA2Report__scoreTitle'>TOTAL SCORE</h2>
                            <div className='digitalTSIA2Report__scorePercent'>
                                {percentOf(testData.rawCorrect, testData.totalQuestionCount)}
                                <span style={{ fontSize: '0.5em', fontWeight: 400 }}>%</span>
                            </div>
                            <div className='digitalTSIA2Report__scoreRaw'>
                                {testData.rawCorrect}/{testData.totalQuestionCount} correct
                            </div>
                        </div>
                        {testData.testSections.map((testSection, t) => (
                            <div className='digitalTSIA2Report__scoreBlock' key={t}>
                                <h3 className='digitalTSIA2Report__sectionScoreTitle'>
                                    {testSection.subject}
                                </h3>
                                <div className='digitalTSIA2Report__scorePercent'>
                                    {percentOf(testSection.rawCorrect, testSection.questionCount)}
                                    <span style={{ fontSize: '0.5em', fontWeight: 400 }}>%</span>
                                </div>
                                <div className='digitalTSIA2Report__scoreRaw'>
                                    {testSection.rawCorrect}/{testSection.questionCount} correct
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className='digitalTSIA2Report__overview'>
                        {testData.testSections.some(
                            section => section.stages.some(stage => stage.rootCategories?.length > 0),
                        ) && (<>
                            <h2 className='digitalTSIA2Report__cardSubTitle'>Category Scores</h2>
                            <div className='digitalTSIA2Report__overviewRow'>
                                {testData.testSections.flatMap((testSection, s) =>
                                    testSection.stages.map(stage => (
                                    <div
                                        className='digitalTSIA2Report__overviewItem'
                                        key={`${testSection.subject}-${stage.moduleName}`}
                                    >
                                        <h4 className='digitalTSIA2Report__overviewLabel'>
                                            {stage.moduleName}
                                        </h4>
                                        {stage.rootCategories.map(rootCategory => (
                                            <React.Fragment key={rootCategory.name}>
                                                <h5
                                                    className={rootCategoryName === `${stage.moduleName}-${rootCategory.name}`
                                                        ? 'digitalTSIA2Report__levelLabel--active'
                                                        : 'digitalTSIA2Report__levelLabel'}
                                                    onClick={() => setRootCategoryName(
                                                        rootCategoryName === `${stage.moduleName}-${rootCategory.name}`
                                                            ? '' : `${stage.moduleName}-${rootCategory.name}`,
                                                    )}
                                                >
                                                    {rootCategory.name}
                                                    <div className={rootCategoryName === `${stage.moduleName}-${rootCategory.name}`
                                                        ? 'digitalTSIA2Report__levelDetailed--active'
                                                        : 'digitalTSIA2Report__levelDetailed'}
                                                    >
                                                        {(rootCategory.categories ?? []).map(category => (
                                                            <div
                                                                className='digitalTSIA2Report__levelDetailedItem'
                                                                key={category.name}
                                                            >
                                                                <p>{category.name}</p>
                                                                <p className={
                                                                    category.numCorrect / category.numQuestions >= 0.8
                                                                        ? 'digitalTSIA2Report__levelDetailedGreen'
                                                                        : category.numCorrect / category.numQuestions >= 0.4
                                                                        ? 'digitalTSIA2Report__levelDetailedYellow'
                                                                        : 'digitalTSIA2Report__levelDetailedRed'
                                                                }>
                                                                    {category.numCorrect}/{category.numQuestions}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </h5>
                                                <LevelBar
                                                    className={s % 2 === 0
                                                        ? 'digitalTSIA2Report__levelGreen'
                                                        : 'digitalTSIA2Report__levelOrange'}
                                                    percent={rootCategory.numQuestions
                                                        ? 100 * rootCategory.numCorrect / rootCategory.numQuestions
                                                        : 0}
                                                    label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                                />
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )))}
                            </div>
                        </>)}
                    </div>
                </div>

                {/* Where the other reports put Section Time Averages. TSIA2 is
                    untimed, so this reports the routing instead. */}
                <h2 className='digitalTSIA2Report__cardSubTitle' style={{ marginTop: 30 }}>
                    Stages Taken
                </h2>
                <div className='digitalTSIA2Report__overviewRow'>
                    {testData.testSections.map((testSection, s) => (
                        <div className='digitalTSIA2Report__overviewItem' key={s}>
                            <h4 className='digitalTSIA2Report__overviewLabel'>
                                {testSection.subject}
                            </h4>
                            <table className='digitalTSIA2Report__stageTable'>
                                <tbody>
                                    {testSection.stages.map(stage => (
                                        <tr key={stage.moduleName}>
                                            <td>{stage.moduleName}</td>
                                            <td>{stage.correct}/{stage.total} correct</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </div>

            {/* Questions Overview: section tabs, then a tab per stage, then
                the answer table -- the same block the other reports carry,
                with Time Spent dropped because the test is untimed. */}
            <div className='digitalTSIA2Report__questions'>
                <h2 className='digitalTSIA2Report__questionsHeader'>Questions Overview</h2>

                <div className='digitalTSIA2Report__sectionTabs'>
                    {testData.testSections.map((testSection, t) => (
                        <button
                            className={sectionIndex === t
                                ? 'digitalTSIA2Report__sectionTab--active'
                                : 'digitalTSIA2Report__sectionTab'}
                            onClick={() => { setSectionIndex(t); setStageIndex(0); }}
                            key={testSection.subject}
                        >
                            {testSection.subject}
                        </button>
                    ))}
                    <div className={sectionIndex === 0
                        ? 'digitalTSIA2Report__sectionTabIndicatorLeft'
                        : 'digitalTSIA2Report__sectionTabIndicatorRight'}
                    />
                </div>

                {/* Only shown when the student was routed: a student who met
                    the cutoff has one stage and needs no tab to choose it. */}
                {activeSection.stages.length > 1 && (
                    <div className='digitalTSIA2Report__moduleTabs'>
                        {activeSection.stages.map((stage, t) => (
                            <button
                                className={stageIndex === t
                                    ? 'digitalTSIA2Report__moduleTab--active'
                                    : 'digitalTSIA2Report__moduleTab'}
                                onClick={() => setStageIndex(t)}
                                key={stage.moduleName}
                            >
                                {stage.moduleName}
                            </button>
                        ))}
                        <div className={stageIndex === 0
                            ? 'digitalTSIA2Report__moduleTabIndicatorLeft'
                            : 'digitalTSIA2Report__moduleTabIndicatorRight'}
                        />
                    </div>
                )}

                {activeStage && (
                    <div className='digitalTSIA2Report__questionsOverview'>
                        <div className='digitalTSIA2Report__questionsSummary'>
                            <div className='digitalTSIA2Report__questionsSummaryItem'>
                                <p className='digitalTSIA2Report__questionSummaryNumber'>
                                    {activeStage.answers.length}
                                </p>
                                <p className='digitalTSIA2Report__questionSummaryText'>
                                    Total Questions
                                </p>
                            </div>
                            <div className='digitalTSIA2Report__questionsSummaryItem'>
                                <p className='digitalTSIA2Report__questionSummaryNumber'>
                                    {activeStage.answers.filter(answer => answer.isCorrect).length}
                                </p>
                                <p className='digitalTSIA2Report__questionSummaryText'>
                                    Correct Answers
                                </p>
                            </div>
                            <div className='digitalTSIA2Report__questionsSummaryItem'>
                                <p className='digitalTSIA2Report__questionSummaryNumber'>
                                    {activeStage.answers.filter(answer => !answer.isCorrect).length}
                                </p>
                                <p className='digitalTSIA2Report__questionSummaryText'>
                                    Incorrect Answers
                                </p>
                            </div>
                        </div>

                        <table className='digitalTSIA2Report__questionsTable'>
                            <tbody>
                                {answerHead}
                                {answerRows(activeStage.answers)}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/*
                Print-only: every stage the student was served, section by
                section, one stage per sheet after the overview. The on-screen
                block above shows one stage at a time, which is right on screen
                and wrong on paper. The @media print rules swap the two for a
                single-report print; a bulk print stays overview-only and hides
                this block (BulkPrintReports/index.css), as the other report
                types do since the question-analysis print change.
            */}
            <div className='digitalTSIA2Report__questionsPrint'>
                {testData.testSections.flatMap(testSection => testSection.stages.map(stage => (
                    <div
                        className='digitalTSIA2Report__questionsPrintModule'
                        key={`print:${testSection.subject}:${stage.moduleName}`}
                    >
                        <p className='digitalTSIA2Report__moduleName'>
                            {testSection.subject} · {stage.moduleName} · {stage.correct}/{stage.total} correct
                        </p>
                        <div className='digitalTSIA2Report__questionsOverview'>
                            <table className='digitalTSIA2Report__questionsTable'>
                                <thead>{answerHead}</thead>
                                <tbody>{answerRows(stage.answers)}</tbody>
                            </table>
                        </div>
                    </div>
                )))}
            </div>
        </div>
    );
}

export default TestTSIA2Report;
